import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { GradeRing, GradeSteps } from '../components/GradeRing.js';
import { HelpButton } from '../components/HelpButton.js';
import { useLocale } from '../components/site.js';
import { LinkButton, LoadError, Loading, Notice, UrgencyBadge } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { useSites } from '../sites.js';

type StepStatus = 'pending' | 'running' | 'done' | 'failed';

const STEP_CLASS: Record<StepStatus, string> = {
  done: 'done',
  running: 'run',
  pending: 'wait',
  failed: 'na',
};

function StepIcon({ status }: { status: StepStatus }) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    'aria-hidden': true,
  } as const;
  if (status === 'done')
    return (
      <svg {...common}>
        <path d="M5 12l4 4L19 6" />
      </svg>
    );
  if (status === 'running')
    return (
      <svg {...common} strokeLinecap="round">
        <path d="M12 3a9 9 0 1 0 9 9" />
      </svg>
    );
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="8" />
    </svg>
  );
}

const UNREACHABLE_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M12 3l10 18H2z" />
    <path d="M12 10v4M12 17h.01" />
  </svg>
);

/**
 * Onboarding (design/pages/app/onboarding.html, US-02): the first checks of a new site run while
 * the user watches, then the grade and the three most important points. It stands outside the
 * site pages, as in the mock-up.
 */
export function Onboarding() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const locale = useLocale();
  const location = useLocation();
  const sites = useSites();
  const host = sites.data?.items.find((s) => s.id === siteId)?.host;
  const progress = useQuery({
    queryKey: ['site', siteId, 'onboarding'],
    queryFn: () => unwrap(api.GET('/sites/{siteId}/onboarding', { params: { path: { siteId } } })),
    refetchInterval: (query) => (query.state.data?.completed ? false : 2000),
  });
  const done = progress.data?.completed === true;
  const overview = useQuery({
    queryKey: ['site', siteId, 'overview', locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/overview', {
          params: { path: { siteId }, query: { locale } },
        }),
      ),
    enabled: done,
    // The score settles about a minute after the last check (debounce).
    refetchInterval: (query) => (query.state.data?.health ? false : 5000),
  });

  if (progress.isPending) return <Loading />;
  if (progress.isError)
    return (
      <LoadError
        message={errorMessage(progress.error, t)}
        onRetry={() => void progress.refetch()}
      />
    );
  const steps = progress.data.steps;
  const finished = steps.filter((s) => s.status === 'done' || s.status === 'failed').length;
  const unreachable =
    (location.state as { unreachable?: boolean } | null)?.unreachable === true ||
    steps.some((s) => s.key === 'availability' && s.status === 'failed');
  const ofTotal = t('onboarding.progress', { done: finished, total: steps.length });
  // Said while the checks run and still with the result: the grade alone hides it.
  const notReachable = unreachable ? (
    <Notice
      kind="warning"
      title={t('onboarding.unreachable')}
      action={
        <LinkButton to={`/sites/${siteId}/help`} variant="secondary" size="sm">
          {t('help.cta')}
        </LinkButton>
      }
    >
      {t('onboarding.unreachableText')}
    </Notice>
  ) : null;

  if (done) {
    const health = overview.data?.health;
    const top = overview.data?.topFindings ?? [];
    return (
      <div className="page">
        <div className="ob">
          <div>
            <h1 className="d2">{t('onboarding.result')}</h1>
            <p className="bodyL mt-2">{host ? `${host} · ${ofTotal}` : ofTotal}</p>
          </div>
          {notReachable}
          <div className="result">
            {health ? (
              <>
                <div className="ring">
                  <GradeRing score={health.score} grade={health.grade} />
                  <div>
                    <div className="num">
                      {health.score}
                      <small>/ 100</small>
                    </div>
                    <div className="cap">
                      {t('onboarding.gradeCap', { grade: health.grade, label: health.label })}
                    </div>
                    {health.nextGrade ? (
                      <div className="cap">
                        {t('onboarding.toNext', {
                          grade: health.nextGrade.grade,
                          count: health.nextGrade.steps.length,
                        })}
                      </div>
                    ) : null}
                  </div>
                </div>
                <GradeSteps grade={health.grade} />
              </>
            ) : (
              <div className="ring">
                <svg viewBox="0 0 100 100" aria-hidden="true">
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    fill="none"
                    stroke="var(--line-strong)"
                    strokeWidth="6"
                  />
                  <text
                    x="50"
                    y="60"
                    textAnchor="middle"
                    fontSize="30"
                    fontWeight="800"
                    fill="var(--fg-3)"
                    fontFamily="Manrope, sans-serif"
                  >
                    …
                  </text>
                </svg>
                <div role="status">
                  <div className="h4s">{t('onboarding.scoring')}</div>
                  <div className="cap">{t('onboarding.scoringText')}</div>
                </div>
              </div>
            )}
            {!overview.data ? (
              <div className="grid gap-2.5" aria-hidden="true">
                <div className="skel w-[70%]" />
                <div className="skel w-[85%]" />
                <div className="skel w-[60%]" />
              </div>
            ) : top.length === 0 ? (
              <Notice kind="success">{t('site.top.empty')}</Notice>
            ) : (
              <>
                <h2>{t('site.top.title')}</h2>
                <ol className="top">
                  {top.map((f, i) => (
                    <li key={f.id}>
                      <div className="top3">
                        <div className="n" aria-hidden="true">
                          {i + 1}
                        </div>
                        <div>
                          <UrgencyBadge urgency={f.urgency} />
                          <h3 className="h4s">{f.title}</h3>
                          {f.summary ? <p>{f.summary}</p> : null}
                          <div className="act">
                            <LinkButton
                              to={`/sites/${siteId}/findings?open=${encodeURIComponent(f.ruleCode)}`}
                              variant="secondary"
                              size="sm"
                            >
                              {t('site.top.view')}
                            </LinkButton>
                            {f.service ? (
                              <HelpButton
                                siteId={siteId}
                                service={f.service}
                                findingId={f.id}
                                ruleCode={f.ruleCode}
                                size="sm"
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </>
            )}
            <div className="row">
              <LinkButton to={`/sites/${siteId}`} variant="primary">
                {t('onboarding.toDashboard')}
              </LinkButton>
              <LinkButton to={`/sites/${siteId}/findings`} variant="secondary">
                {t('onboarding.allFindings')}
              </LinkButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="ob">
        <div>
          <h1 className="d2">
            {host ? t('onboarding.checking', { host }) : t('onboarding.title')}
          </h1>
          <p className="bodyL mt-2">{ofTotal}</p>
        </div>
        {notReachable}
        <div
          className="progress"
          role="progressbar"
          aria-label={t('onboarding.progressLabel')}
          aria-valuemin={0}
          aria-valuemax={steps.length}
          aria-valuenow={finished}
        >
          <i style={{ width: `${Math.round((finished / steps.length) * 100)}%` }} />
        </div>
        <ol className="steps" aria-live="polite">
          {steps.map((step) => {
            const notReached = step.key === 'availability' && step.status === 'failed';
            return (
              <li key={step.key} className={STEP_CLASS[step.status]}>
                {notReached ? UNREACHABLE_ICON : <StepIcon status={step.status} />}
                <span>{t(`onboarding.steps.${step.key}`)}</span>
                <span className={notReached ? 'st unreachable' : 'st'}>
                  {notReached
                    ? t('onboarding.status.unreachable')
                    : t(`onboarding.status.${step.status}`)}
                </span>
              </li>
            );
          })}
        </ol>
        <Notice
          kind="info"
          title={t('onboarding.canLeave')}
          action={
            <LinkButton to="/" variant="ghost" size="sm">
              {t('onboarding.toOverview')}
            </LinkButton>
          }
        >
          {t('onboarding.canLeaveText')}
        </Notice>
      </div>
    </div>
  );
}
